import { ComboBox, Dialog, Icon, Stack, Text, TextField } from '@fluentui/react';
import { Button, ClipboardButton, useClassNames, useTheme } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { roleBindingResourceCompile } from '../../../authz/authz.functions';
import { useQueryPermissionCurrentUser } from '../../../authz/authz.hooks';
import { Group } from '../../../authz/gen/ai/h2o/user/v1/group_pb';
import { User } from '../../../authz/gen/ai/h2o/user/v1/user_pb';
import { useRoles } from '../../../authz/providers/RoleProvider';
import { WorkspacesUserActionType } from '../../../authz/userActions';
import { MembersDropdown } from '../../ManageWorkspaces/components/ManageMembers/MembersDropdown';
import { useWorkspaces } from '../../Orchestrator/WorkspaceProvider';
import { IShareModal, IShareModalClassNames, shareModalStyle } from './ShareModal.style';

const DRIVE_READER_ROLE = 'roles/drive-reader';
const DRIVE_WRITER_ROLE = 'roles/drive-writer';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  isDirectory?: boolean;
  itemKey?: string;
}

export const ShareModal = (props: ShareModalProps) => {
  const { isOpen, onClose, shareUrl, isDirectory, itemKey } = props;
  const { createRoleBinding } = useRoles();
  const { activeWorkspace } = useWorkspaces();
  const [selectedMember, setSelectedMember] = useState<User | Group | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(DRIVE_READER_ROLE);

  const theme = useTheme();
  const classNames = useClassNames<IShareModal, IShareModalClassNames>('ShareModal', shareModalStyle);
  const styles = shareModalStyle(theme);

  // Check if user can edit workspace (required to grant access)
  const canEditWorkspaceQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_EDIT,
    resource: roleBindingResourceCompile(activeWorkspace?.name || ''),
  });
  const canEditWorkspace = Boolean(canEditWorkspaceQuery.data?.allowed);

  // Reset state when modal opens or when the share URLs change (e.g., workspace switch)
  useEffect(() => {
    if (isOpen) {
      setSelectedMember(null);
      // reset role selection
      setSelectedRole(DRIVE_READER_ROLE);
    }
  }, [isOpen, shareUrl, itemKey]);

  const handleGrantAccess = async () => {
    if (!selectedMember) return;

    try {
      const memberId = 'name' in selectedMember && selectedMember.name ? selectedMember.name : '';
      if (!memberId) return;

      // Grant selected role from combobox
      await createRoleBinding(selectedRole, memberId);

      // Reset selection after successful grant
      setSelectedMember(null);
      // reset role selection
      setSelectedRole(DRIVE_READER_ROLE);
    } catch (error) {
      console.error('Failed to grant access:', error);
    }
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onClose}
      minWidth={600}
      dialogContentProps={{
        type: 0,
        title: `Share the access to the Workspace`,
        closeButtonAriaLabel: 'Close',
        showCloseButton: true,
      }}
    >
      <Stack tokens={{ childrenGap: 12 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Icon iconName="Link" className={classNames.workspaceLinkIcon} />
          <Text className={classNames.sectionHeader}>Workspace Link</Text>
        </Stack>

        <Stack className={classNames.sectionCard} tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={classNames.warningBanner}>
            <Icon iconName="Warning" className={classNames.warningIcon} />
            <Text className={classNames.warningText}>
              {canEditWorkspace
                ? `Granting access gives permission to the entire Workspace Drive, not just this ${
                    isDirectory ? 'directory' : 'file'
                  }.`
                : `Recipients need workspace access to view this ${isDirectory ? 'directory' : 'file'}.`}
            </Text>
          </Stack>

          {canEditWorkspace && (
            <Stack tokens={{ childrenGap: 12 }}>
              <Text styles={{ root: { fontWeight: 600 } }}>Grant Access</Text>
              <Text variant="small" styles={{ root: { color: 'var(--h2o-gray600)' } }}>
                Add members who can access this Workspace.
              </Text>

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="small" styles={{ root: { marginBottom: 4, fontWeight: 600 } }}>
                  Select member
                </Text>

                <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <MembersDropdown
                      onUserChange={(option) => setSelectedMember(option as User | Group)}
                      prepopulatedUser={selectedMember as User | undefined}
                      fetchGroups
                    />

                    <ComboBox
                      selectedKey={selectedRole}
                      options={[
                        { key: DRIVE_READER_ROLE, text: 'Reader' },
                        { key: DRIVE_WRITER_ROLE, text: 'Writer' },
                      ]}
                      onChange={(_, option) => option && setSelectedRole(option.key as string)}
                      disabled={!selectedMember}
                      useComboBoxAsMenuWidth
                    />
                  </Stack>

                  <Button
                    text="Grant Access"
                    iconName="AddFriend"
                    onClick={handleGrantAccess}
                    disabled={!selectedMember}
                    styles={styles.grantButton}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}

          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <TextField value={shareUrl} readOnly styles={styles.textField} data-test="share-url-input" />
            <ClipboardButton content={shareUrl} data-test="copy-url-button" />
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};
